/**
 * Editorial content for /enterprise.
 *
 * Kept out of the section components for the same reason as `aboutContent`:
 * the copy on this page gets revised far more often than its layout, and the
 * claims need to stay reconcilable with the server-rendered body in
 * `backend/config/publicPageSeoContent.js`.
 */
import { MODEL_TOTAL, PROVIDER_TOTAL } from './modelInventory.generated';

export const CONTACT_EMAIL = 'ai@gab.com';

/** Scope chips under the hero: the shape of an engagement in a few words. */
export const ENGAGEMENT_SCOPE = [
  { icon: 'robot', label: 'Custom assistants' },
  { icon: 'brain', label: 'Fine-tuning' },
  { icon: 'plug', label: 'Integration' },
  { icon: 'shield-halved', label: 'Private deployment' },
  { icon: 'chart-line', label: 'Analytics' },
  { icon: 'wand-magic-sparkles', label: 'Generative apps' },
];

export const HERO_MARQUEE = [
  'Custom model work',
  'On-premise',
  'Private cloud',
  'Air-gapped',
  'System integration',
  'Volume pricing',
  'Dedicated engineers',
  'US-based team',
];

/**
 * Figures are deliberately ones we can point at: the founding year, the live
 * model catalogue, and where the team sits. No invented client counts.
 */
export const ENGAGEMENT_FIGURES = [
  { value: '2016', label: 'Shipping at scale since' },
  { value: String(MODEL_TOTAL), label: 'Models available to build on' },
  { value: String(PROVIDER_TOTAL), label: 'Providers, so no lock-in' },
  { value: 'USA', label: 'Team, support, and deployment' },
];

export const SERVICES = [
  {
    icon: 'robot',
    title: 'Custom AI assistants',
    copy: 'Purpose-built assistants trained on your data, workflows, and domain expertise — deployed internally or on customer-facing channels.',
    points: [
      'Internal knowledge base assistants',
      'Customer support automation',
      'Domain-specific copilots',
      'Multi-channel deployment',
    ],
  },
  {
    icon: 'brain',
    title: 'Model fine-tuning and training',
    copy: 'Tuning and evaluation against your proprietary data, so accuracy is measured on your tasks rather than a public benchmark.',
    points: [
      'Custom model fine-tuning',
      'RAG pipeline architecture',
      'Domain-specific embeddings',
      'Benchmarking on your tasks',
    ],
  },
  {
    icon: 'plug',
    title: 'Integration and automation',
    copy: 'Connecting AI to the systems your team already runs, so it lands inside existing workflows instead of beside them.',
    points: [
      'API and system integration',
      'Workflow automation',
      'Legacy system modernization',
      'Data pipeline engineering',
    ],
  },
  {
    icon: 'shield-halved',
    title: 'Private deployment',
    copy: 'On-premise, private cloud, or air-gapped installs. We design around the compliance regime you answer to — SOC 2, HIPAA, or your own internal review.',
    points: [
      'On-premise deployment',
      'Private cloud infrastructure',
      'Air-gapped environments',
      'Data sovereignty controls',
    ],
  },
  {
    icon: 'chart-line',
    title: 'Analytics and intelligence',
    copy: 'Visibility into how AI is actually being used across teams, plus the predictive and reporting layers built on top of your own data.',
    points: [
      'Predictive analytics models',
      'Usage reporting across teams',
      'Anomaly detection',
      'Business intelligence automation',
    ],
  },
  {
    icon: 'wand-magic-sparkles',
    title: 'Generative applications',
    copy: 'Content, code, document, and media pipelines built for production volume rather than a demo.',
    points: [
      'Content generation at scale',
      'Document processing and extraction',
      'Code generation tools',
      'Creative asset production',
    ],
  },
];

export const PROCESS = [
  {
    title: 'Discovery',
    copy: 'We learn your business, find the high-impact opportunities, and agree on measurable success criteria before anyone writes code.',
  },
  {
    title: 'Architecture',
    copy: 'Our engineers design the system — model selection, data pipelines, and how it integrates with what you already run.',
  },
  {
    title: 'Build and train',
    copy: 'Development in short iterations with feedback loops, training custom models on your data where it earns its keep.',
  },
  {
    title: 'Deploy',
    copy: 'Production rollout to your infrastructure with monitoring, testing, and performance validation against the criteria from step one.',
  },
  {
    title: 'Optimize and support',
    copy: 'Ongoing monitoring, retraining, and direct access to the engineers who built it. AI is not set-and-forget.',
  },
];

export const DIFFERENTIATORS = [
  {
    icon: 'code',
    title: 'Proven at scale',
    copy: 'Over a decade building and operating services used by tens of millions of people. We know what it takes to ship and keep it running.',
  },
  {
    icon: 'bolt',
    title: 'Rapid delivery',
    copy: 'Most projects go from kickoff to production in weeks, not months. You get working AI rather than a planning phase.',
  },
  {
    icon: 'lock',
    title: 'Security first',
    copy: 'Your data stays yours. On-premise, private cloud, and air-gapped deployments with full data sovereignty.',
  },
  {
    icon: 'arrows-rotate',
    title: 'Continuous improvement',
    copy: 'We monitor, retrain, and optimize after launch so the system gets better instead of quietly degrading.',
  },
  {
    icon: 'handshake',
    title: 'True partnership',
    copy: 'We work as an extension of your team — direct access to senior engineers, transparent communication, shared goals.',
  },
  {
    icon: 'microchip',
    title: 'Model agnostic',
    copy: `Open source, commercial, or custom trained — we use the best model for the job across ${PROVIDER_TOTAL} providers. No vendor lock-in.`,
  },
];

export const INDUSTRIES = [
  {
    icon: 'building',
    industry: 'Financial services',
    examples: ['Compliance monitoring', 'Fraud and risk analysis', 'Document processing'],
  },
  {
    icon: 'heart-pulse',
    industry: 'Healthcare',
    examples: ['Clinical decision support', 'Medical record extraction', 'Patient communication'],
  },
  {
    icon: 'scale-balanced',
    industry: 'Legal',
    examples: ['Contract analysis and review', 'Research assistants', 'Due diligence automation'],
  },
  {
    icon: 'cart-shopping',
    industry: 'E-commerce and retail',
    examples: ['Product recommendations', 'Customer service agents', 'Demand forecasting'],
  },
  {
    icon: 'graduation-cap',
    industry: 'Education',
    examples: ['Adaptive learning', 'Grading and feedback', 'Content generation at scale'],
  },
  {
    icon: 'industry',
    industry: 'Manufacturing',
    examples: ['Predictive maintenance', 'Quality control', 'Supply chain optimization'],
  },
  {
    icon: 'landmark',
    industry: 'Government',
    examples: ['Citizen service automation', 'Document analysis', 'On-premise deployment'],
  },
  {
    icon: 'newspaper',
    industry: 'Media and publishing',
    examples: ['Content generation', 'Audience analytics', 'Automated moderation'],
  },
];

export const CLOSING_MARKS = [
  'Free consultation',
  'Quoted per engagement',
  'US-based team',
];

export const CLOSING_TRUST = [
  'Senior engineers, not account managers',
  'Volume credit pricing',
  'NDA on request',
];
